import React, { useState, useEffect, useCallback, useMemo } from 'react'
import label from '../../../../../../assets/i18n/en.json'
import EquipmentFormConfig from './equipment-form-config'
import AppointmentService from '../../../../../services/api/appointment.service';
import EquipmentService from '../../../../../services/api/equipment.service';
import PracticeLocationSelector from '../../../../templates/components/PracticeLocationSelector';
import HPTSelect from '../../../../templates/components/HPTSelect';
import ModalBox from '../../../../templates/components/ModalBox';
import { useGetAllPracticeServiceTypes } from '../../../../../services/cache/CacheService';
import PracticeLocationServiceTypesService from '../../../../../services/api/practice-location-service-types.service';
import FormValidatorService from '../../../../../services/validator/form-validator.service';
import toast from "react-hot-toast";

const EquipmentForm = (props) => {
    // Pull properties form parent
    const { initialData, submitHandler } = props;

    const [isLoader, setIsLoader] = useState(false)
    const [formData, setFormData] = useState(initialData)
    const [practiceLocationRooms, setPracticeLocationRooms] = useState([])
    const [equipmentTypes, setEquipmentTypes] = useState()
    
    // Practice Service Types state
    const stateAllPracticeServiceTypeList = useGetAllPracticeServiceTypes()
    const [allServiceTypeList, setAllServiceTypeList] = useState([])
    const [locationServiceTypes, setLocationServiceTypes] = useState([])
    const [activeServiceTypes, setActiveServiceTypes] = useState([])
    const [addAll, setAddAll] = useState(false)
    const [deleteAll, setDeleteAll] = useState(false)
    const [loadingLocationServices, setLoadingLocationServices] = useState(false)
    const required = ['description', 'equipmentTypeId']
    const [errors, setErrors] = useState({})
    const equipmentTypeLookup = () => {
        EquipmentService.equipmentTypeLookup()
            .then((response) => {
                setEquipmentTypes(response);
            })
            .catch(error => {
                // console.log(error)
                // setCheckException(error);
            })
    }
    const onLocationChanged = (newLocationId) => {
        inputChange({ target: { value: '', name: 'practiceLocationRoomId' } })
        inputChange({ target: { value: newLocationId, name: 'practiceLocationId' } });
    }
    useEffect(() => {
        if (equipmentTypes && !equipmentTypes.find(obj => obj.equipmentTypeId === formData.equipmentTypeId)) {
            formData.equipmentTypeId = null;
        }
    }, [equipmentTypes]);

    useEffect(() => {
        AppointmentService.practiceLocationRoomLookup(formData.practiceLocationId)
            .then(
                (response) => {
                    setPracticeLocationRooms(response);
                    setIsLoader(false);
                })
            .catch(error => {
                setIsLoader(false);
            })
    }, [formData.practiceLocationId])

    useEffect(() => {
        equipmentTypeLookup()
    }, [])

    // Fetch location service types when location changes
    useEffect(() => {
        if (formData?.practiceLocationId) {
            getPracticeLocationServiceTypes()
        } 
        else {
            setLocationServiceTypes([]);
            setAllServiceTypeList([]);
            setActiveServiceTypes([]);
        }
    }, [formData?.practiceLocationId])

    const getPracticeLocationServiceTypes = () => {
        if (!formData?.practiceLocationId) {
            return;
        }

        setLoadingLocationServices(true);
        PracticeLocationServiceTypesService.getPossibleServiceTypesByLocation(formData.practiceLocationId)
            .then((selectedServices) => {
                //console.log("Fetched location service types:", selectedServices);
                setLocationServiceTypes(selectedServices);
            })
            .catch(error => {
                //console.error("Failed to fetch location service types:", error);
                setLocationServiceTypes([]);
            })
            .finally(() => {
                setLoadingLocationServices(false);
            });
    }

    const initializeEquipmentServices = () => {
        if (!formData?.practiceLocationId) {
            setAllServiceTypeList([]);
            setActiveServiceTypes([]);
            return;
        }

        // Start with location service types (these are available at this location)
        const locationBasedServices = locationServiceTypes.map(service => ({
            ...service,
            isActive: false,
            isLocationActive: true,
            displayName: service.practiceServiceType
        }));

        let finalServiceList = [...locationBasedServices];
        let equipmentActiveServices = [];

        // If equipment has existing services, process them
        if (props.isEdit && formData.equipmentServices && formData.equipmentServices.length > 0) {
            const existingEquipmentServiceIds = formData.equipmentServices.map(service => service.practiceServiceTypeId);
            
            // Mark location services as active if they're in equipment services
            finalServiceList = locationBasedServices.map(service => ({
                ...service,
                isActive: existingEquipmentServiceIds.includes(service.practiceServiceTypeId)
            }));

            // Find equipment services that are NOT in location services (inactive at this location)
            const inactiveEquipmentServices = formData.equipmentServices.filter(equipService => 
                !locationServiceTypes.some(locService => locService.practiceServiceTypeId === equipService.practiceServiceTypeId)
            );

            // For inactive equipment services, get their names from stateAllPracticeServiceTypeList
            if (inactiveEquipmentServices.length > 0 && stateAllPracticeServiceTypeList) {
                const inactiveServicesWithNames = inactiveEquipmentServices.map(equipService => {
                    const serviceDetails = stateAllPracticeServiceTypeList.find(allService => 
                        allService.practiceServiceTypeId === equipService.practiceServiceTypeId
                    );
                    return {
                        ...equipService,
                        practiceServiceType: serviceDetails?.practiceServiceType || 'Unknown Service',
                        isActive: true,
                        isLocationActive: false,
                        displayName: `${serviceDetails?.practiceServiceType || 'Unknown Service'} (inactive)`
                    };
                });
                
                // Add inactive services to the list
                finalServiceList = [...finalServiceList, ...inactiveServicesWithNames];
            }

            equipmentActiveServices = finalServiceList.filter(service => service.isActive);
        }
        //  console.log("Final Service List:", finalServiceList);
        //  console.log("Active Equipment Services:", equipmentActiveServices);
        setAllServiceTypeList(finalServiceList);
        setActiveServiceTypes(equipmentActiveServices);
    }

    useEffect(() => {
        if(locationServiceTypes && locationServiceTypes.length > 0){
            initializeEquipmentServices();
        }else{
            setAllServiceTypeList([]);
            setActiveServiceTypes([]);
        }
    }, [locationServiceTypes])
    // Update formData when active service types change
    useEffect(() => {
        const activeIds = activeServiceTypes.map(service => service.practiceServiceTypeId).join(',');
        setFormData(prev => ({
            ...prev,
            practiceServiceTypeIds: activeIds
        }));
    }, [activeServiceTypes])

    // formula for input change
    const inputChange = (e) => {
        let newStateObject = { ...formData };
        newStateObject[e.target.name] = e.target.value
        setErrors(FormValidatorService.setErrors(e, errors, EquipmentFormConfig.config))
        setFormData(newStateObject);
        return console.log(formData)
    };

    // Practice Service Types functions
    const addAllServices = useCallback((list) => {
        const servicesToAdd = list || allServiceTypeList.filter(obj => !obj.isActive && obj.isLocationActive);
        const updatedList = allServiceTypeList.map(service => {
            const shouldActivate = servicesToAdd.some(s => s.practiceServiceTypeId === service.practiceServiceTypeId);
            return shouldActivate ? { ...service, isActive: true } : service;
        });
        //console.log("Updated List after addition:", updatedList);
        setAllServiceTypeList(updatedList);
        setActiveServiceTypes(updatedList.filter(service => service.isActive));
    }, [allServiceTypeList]);

    const deleteAllServices = useCallback((list) => {
        const servicesToRemove = list || activeServiceTypes;
        const idsToRemove = servicesToRemove.map(service => service.practiceServiceTypeId);
        const updatedList = allServiceTypeList.map(service => 
            idsToRemove.includes(service.practiceServiceTypeId) 
                ? { ...service, isActive: false } 
                : service
        );
        //console.log("Updated List after deletion:", updatedList);
        setAllServiceTypeList(updatedList);
        setActiveServiceTypes(updatedList.filter(service => service.isActive));
    }, [allServiceTypeList, activeServiceTypes]);

    const addSingleService = useCallback((service) => {
        addAllServices([service]);
    }, [addAllServices]);

    const removeSingleService = useCallback((service) => {
        deleteAllServices([service]);
    }, [deleteAllServices]);

    return (
        <div>
            {isLoader && <div className="ui">
                <div className="ui active dimmer">
                    <div className="ui indeterminate text loader">{label.common.processing}</div>
                </div>
            </div>}
            <form className=''>
                <div className="ui content">
            <div className="row">

                        <div className="required field col-md-6 col-12">
                            <label> {label.equipment.add.equipmentName}</label>
                            <input placeholder="Equipment" type="text" name="description" value={formData.description} onChange={(e) => { e.preventDefault(); inputChange(e) }} />
                            <span className="error-message">{errors.description}</span>
                        </div>
                        <div className="required field col-md-6 col-12">
                            <label>{label.equipment.add.equipmentTypeName}</label>
                            <HPTSelect
                                className="react-select-container"
                                classNamePrefix="react-select"
                                options={equipmentTypes}
                                name="equipmentTypeId"
                                value={equipmentTypes && equipmentTypes.find(obj => obj.equipmentTypeId === formData.equipmentTypeId)}
                                onChange={e => {
                                    inputChange({
                                        target:
                                            { value: e.equipmentTypeId, name: 'equipmentTypeId' }
                                    })
                                }}
                                getOptionLabel={(option) => option.equipmentType}
                                getOptionValue={(option) => option.equipmentTypeId}
                            />
                        </div>
                        <div className="required field col-md-6 col-12">
                            <label>Practice Location</label>
                            <PracticeLocationSelector locationId={formData?.practiceLocationId ? formData?.practiceLocationId : null} disabled={props.submitLabel === "Update" ? true : false} onSelected={onLocationChanged} dontResetState={true} />
                        </div>
                        <div className="field col-md-6 col-12">
                            <label>Practice Location Room</label>
                            <HPTSelect
                                className="react-select-container"
                                classNamePrefix="react-select"
                                value={practiceLocationRooms && practiceLocationRooms.find(obj => obj.practiceLocationRoomId === formData.practiceLocationRoomId)}
                                options={practiceLocationRooms}
                                onChange={e => { e ? inputChange({ target: { value: e.practiceLocationRoomId, name: 'practiceLocationRoomId' } }) : inputChange({ target: { value: '', name: 'practiceLocationRoomId' } }) }}
                                isClearable={true}
                                getOptionLabel={(option) => option.room }
                                getOptionValue={(option) => option.practiceLocationRoomId}
                            />
                        </div>
                        
                        {/* Practice Service Types Accordion */}
                        <div className="field mt-4">
                            <label>Manage Linked Services</label>
                            <div className='d-flex row'>
                                <div className='col-md-4 col-12'>
                                    <div className='card'>
                                        <div className='card-header'>
                                            <div className="row d-flex align-items-center justify-content-between">
                                                <span className='col-md-auto col-12'>Linked to Equipment</span>
                                                <div className='col-md-auto col-12'>
                                                    <button 
                                                        type="button"
                                                        className="btn btn-primary" 
                                                        onClick={e => { e.preventDefault(); setDeleteAll(true) }}
                                                        disabled={activeServiceTypes.length === 0}
                                                    >
                                                        Delete All
                                                    </button>
                                                </div>
                                            </div>
                                        </div>
                                        <div className='card-body row d-flex'>
                                            {activeServiceTypes.map(service => (
                                                <div key={service.practiceServiceTypeId} className='col-md-6 col-12 mb-2'>
                                                    <span 
                                                        title="Remove from Equipment" 
                                                        className='text-start point' 
                                                        onClick={e => { e.preventDefault(); 
                                                            removeSingleService(service) 
                                                        }}
                                                    >
                                                        <i className='icon minus small position-relative'/> {service.displayName || service.practiceServiceType}
                                                    </span>
                                                </div>
                                            ))}
                                            {activeServiceTypes.length === 0 && (
                                                <div className='col-12 text-muted'>No service is linked to this equipment.</div>
                                            )}
                                        </div>
                                    </div>
                                </div>
                                <div className='col-md-8 col-12'>
                                    <div className='card'>
                                        <div className='card-header'>
                                            <div className="row d-flex align-items-center justify-content-between">
                                                <span className='col-md-auto col-12'>Available Location Services</span>
                                                <div className='col-md-auto col-12'>
                                                    <button 
                                                        type="button"
                                                        className="btn btn-primary" 
                                                        onClick={e => { e.preventDefault(); setAddAll(true) }}
                                                        disabled={allServiceTypeList.filter(obj => !obj.isActive && obj.isLocationActive).length === 0}
                                                    >
                                                        Add All
                                                    </button>
                                                </div>
                                            </div>
                                        </div>
                                        <div className='card-body row d-flex'>
                                            {allServiceTypeList.filter(obj => !obj.isActive && obj.isLocationActive).map(service => (
                                                <div key={service.practiceServiceTypeId} className='col-md-3 col-12 mb-2'>
                                                    <span 
                                                        title="Add to Equipment" 
                                                        className='text-start point' 
                                                        onClick={e => { e.preventDefault();
                                                            addSingleService(service) 
                                                        }}
                                                    >
                                                        <i className='icon plus small position-relative'/> {service.practiceServiceType}
                                                    </span>
                                                </div>
                                            ))}
                                            {allServiceTypeList.filter(obj => !obj.isActive && obj.isLocationActive).length === 0 && (
                                                <div className='col-12 text-muted'>
                                                    {formData?.practiceLocationId 
                                                        ? "All location service(s) are linked to this equipment." 
                                                        : "Please select a practice location first"
                                                    }
                                                </div>
                                            )}
                                        </div>
                                    </div>
                                </div>
                            </div>
                        </div>
                    </div>
                </div >
            </form >
            
            {/* Add All Confirmation Modal */}
            <ModalBox open={addAll} onClose={() => { setAddAll(false) }} title="Confirm">
                <div className="d-flex row justify-content-between">
                    <div className="col-12 mb-3">
                        Are you sure you want to add all available practice services for this location?
                    </div>
                    <div className="col-auto">
                        <button 
                            className="btn btn-primary" 
                            onClick={e => { 
                                e.preventDefault(); 
                                setAddAll(false); 
                                addAllServices(); 
                            }}
                        >
                            Confirm
                        </button>
                    </div>
                    <div className="col-auto">
                        <button 
                            className="btn btn-secondary" 
                            onClick={e => { e.preventDefault(); setAddAll(false) }}
                        >
                            Cancel
                        </button>
                    </div>
                </div>
            </ModalBox>
            
            {/* Delete All Confirmation Modal */}
            <ModalBox open={deleteAll} onClose={() => { setDeleteAll(false) }} title="Confirm">
                <div className="d-flex row justify-content-between">
                    <div className="col-12 mb-3">
                        Are you sure you want to delete all practice services?
                    </div>
                    <div className="col-auto">
                        <button 
                            className="btn btn-primary" 
                            onClick={e => { 
                                e.preventDefault(); 
                                setDeleteAll(false); 
                                deleteAllServices(); 
                            }}
                        >
                            Confirm
                        </button>
                    </div>
                    <div className="col-auto">
                        <button 
                            className="btn btn-secondary" 
                            onClick={e => { e.preventDefault(); setDeleteAll(false) }}
                        >
                            Cancel
                        </button>
                    </div>
                </div>
            </ModalBox>
            
            <div className="mt-3 d-flex justify-content-between modal-button-bar">
                <div className='col-auto'>
                    <button className="btn btn-primary" onClick={(e) => { 
                        e.preventDefault(); 
                        if (!FormValidatorService.checkForm(errors, formData, required)) {
                            toast.error("Please make sure the form is complete")
                        }
                        else {
                        submitHandler(formData) 
                        }
                        }}>{props.submitLabel}</button>
                </div>
                <div className='col-auto'>
                    <button className="btn btn-secondary" onClick={(e) => { e.preventDefault(); props.onClose() }}> Cancel </button>
                </div>
            </div >
        </div >
    )
}

export default EquipmentForm